import { Button, Col, Flex, Form, Input, Row } from "antd";
import { ReactNode, useState, useRef, useEffect } from "react";
import { useMutation } from "@apollo/client";
import ReCAPTCHA from "react-google-recaptcha";
import { InfoCircleTwoTone } from "@ant-design/icons";
import HeroScreen from "../components/HeroScreen/heroScreen";
import Layout from "../components/layout";
import { SEND_SUPPORT_EMAIL } from "../../components/AssistanceModal/assistanceModal.qql";

const requiredMark = (
  label: ReactNode,
  { required }: { required: boolean }
) => {
  return required ? (
    <>
      {label}
      <span className="text-[#C02B02] ml-[4px]">*</span>
    </>
  ) : (
    label
  );
};

const Contacts = () => {
  const [isSubmitted, setisSubmitted] = useState(false);
  const [recaptchaToken, setRecaptchaToken] = useState<string | null>(null);
  const recaptchaRef = useRef<ReCAPTCHA>(null);
  const [isError, setIsError] = useState<boolean>(false);
  const [sendSupportEmail, { loading }] = useMutation(SEND_SUPPORT_EMAIL, {
    fetchPolicy: "no-cache",
    onCompleted() {
      setisSubmitted(true);
    },
    onError() {
      setIsError(true);
    },
  });

  const handleRecaptchaChange = (token: string | null) => {
    setRecaptchaToken(token);
  };

  const handleSubmit = (values: any) => {
    if (!recaptchaToken) {
      // Trigger reCAPTCHA if not already completed
      recaptchaRef.current?.execute();
      return;
    }

    sendSupportEmail({
      variables: {
        ...values,
        recaptchaToken,
      },
    });
  };

  useEffect(() => {
    if (isError) {
      const timer = setTimeout(() => {
        setIsError(false);
      }, 3000);

      return () => clearTimeout(timer);
    }
  }, [isError]);

  return (
    <Layout>
      <HeroScreen imgSrc="/landing/contacts-hero.png">
        <div className="desktop_pt-[9rem] desktop_pb-[6rem]">
          <h1 className="font-bold text-[40px] leading-[1.2] mb-[1.5rem]">
            Possiamo aiutarti?
            <br />
            Siamo qui per te
          </h1>
          <p className="text-base mb-0">
            Puoi raggiungerci sempre a{" "}
            <a href="mailto:supporto@inbuonasalute.eu">
              supporto@inbuonasalute.eu
            </a>
          </p>
          {isError && (
            <Flex
              align="center"
              className="text-[#c02b0a] bg-[#c02b0a08] border-[#c02b0a40] border-solid rounded-[3px] py-5 px-4"
              gap={12}
            >
              <InfoCircleTwoTone
                className="text-[18px]"
                twoToneColor="#c02b0a"
              />
              <span>
                C&apos;è stato un problema con il tuo invio. Verifica i campi
                qui sotto.
              </span>
            </Flex>
          )}
          {!isSubmitted ? (
            <Form
              name="contact"
              layout="vertical"
              onFinish={handleSubmit}
              autoComplete="off"
              requiredMark={requiredMark}
              className="mt-[40px]"
            >
              <Row gutter={[16, 16]}>
                <Col span={12}>
                  <Form.Item
                    label="Nome"
                    name="name"
                    rules={[
                      {
                        required: true,
                        message: "Questo campo è obbligatorio.",
                      },
                    ]}
                  >
                    <Input />
                  </Form.Item>
                </Col>
                <Col span={12}>
                  <Form.Item
                    label="Cognome"
                    name="surname"
                    rules={[
                      {
                        required: true,
                        message: "Questo campo è obbligatorio.",
                      },
                    ]}
                  >
                    <Input />
                  </Form.Item>
                </Col>
                <Col span={24}>
                  <Form.Item
                    label="Email"
                    name="email"
                    rules={[
                      {
                        type: "email",
                        required: true,
                        message: "Questo campo è obbligatorio.",
                      },
                    ]}
                  >
                    <Input />
                  </Form.Item>
                </Col>
                <Col span={24}>
                  <Form.Item
                    label="Messaggio"
                    name="message"
                    rules={[
                      {
                        required: true,
                        message: "Questo campo è obbligatorio.",
                      },
                    ]}
                  >
                    <Input.TextArea rows={8} />
                  </Form.Item>
                </Col>
              </Row>

              <Form.Item label={null} className="mt-[24px]">
                <Button
                  size="large"
                  loading={loading}
                  disabled={loading}
                  htmlType="submit"
                  block
                >
                  Invia il tuo messaggio
                </Button>
              </Form.Item>
              <ReCAPTCHA
                ref={recaptchaRef}
                sitekey={process.env.REACT_APP_RECAPTCHA_SITE_KEY || ""}
                onChange={handleRecaptchaChange}
                size="invisible"
              />
            </Form>
          ) : (
            <span className="text-base">
              Grazie per averci contattato! Risponderemo il prima possibile.
            </span>
          )}
        </div>
      </HeroScreen>
    </Layout>
  );
};

export default Contacts;
